/*
 * Copyright 2020 Google Inc. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software
 *  distributed under the License is distributed on an "AS IS" BASIS,
 *  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *  See the License for the specific language governing permissions and
 *  limitations under the License.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Message from './message/Message';
import MessageChannel from './message/MessageChannel';
import { privateKeyDump } from './Helpers';
import AdbConnectionInformation from './AdbConnectionInformation';
import Stream from './Stream';
import Shell from './Shell';
const VERSION = 0x01000000;
const VERSION_NO_CHECKSUM = 0x01000001;
const MAX_PAYLOAD = 256 * 1024;
const MACHINE_BANNER = 'host::\0';
export default class AdbClient {
    /**
     * Creates a new AdbClient
     *
     * @param {Transport} transport the transport layer.
     */
    constructor(transport, options, keyStore) {
        this.transport = transport;
        this.options = options;
        this.keyStore = keyStore;
        this.messageCallback = null;
        this.openStreams = new Set();
        this.messageChannel = new MessageChannel(transport, options, this);
    }
    registerStream(stream) {
        this.openStreams.add(stream);
        console.log(this.openStreams);
    }
    unregisterStream(stream) {
        this.openStreams.delete(stream);
        console.log(this.openStreams);
    }
    newMessage(msg) {
        // Check if this message matches one of the open streams.
        for (const stream of this.openStreams) {
            if (stream.consumeMessage(msg)) {
                return;
            }
        }
        if (this.messageCallback) {
            this.messageCallback(msg);
        }
    }
    awaitMessage() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                this.messageCallback = resolve;
            });
        });
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            const version = this.options.useChecksum ? VERSION : VERSION_NO_CHECKSUM;
            const cnxn = Message.cnxn(version, MAX_PAYLOAD, MACHINE_BANNER, this.options.useChecksum);
            yield this.sendMessage(cnxn); // Send the Message
            // Response to connect must be CNXN or AUTH. Ignore different responses until the right one
            // arrives.
            let response;
            do {
                response = yield this.awaitMessage();
            } while (response.header.cmd !== 'CNXN' && response.header.cmd !== 'AUTH');
            // Server connected
            if (response.header.cmd === 'CNXN') {
                if (!response.data) {
                    throw new Error('Connection doesn\'t have data');
                }
                return AdbConnectionInformation.fromDataView(response.data);
            }
            // Server asked to authenticate
            response = yield this.doAuth(response);
            if (!response.data) {
                throw new Error('Connection doesn\'t have data');
            }
            return AdbConnectionInformation.fromDataView(response.data);
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.messageChannel.close();
        });
    }
    shell(command) {
        return __awaiter(this, void 0, void 0, function* () {
            const stream = yield Stream.open(this, `shell:${command}`, this.options);
            const response = yield stream.read();
            yield stream.close();
            return response.dataAsString() || '';
        });
    }
    interactiveShell(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const stream = yield Stream.open(this, 'shell:', this.options);
            return new Shell(stream, callback);
        });
    }
    sync() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield Stream.open(this, 'sync:', this.options);
        });
    }
    pull(filename) {
        return __awaiter(this, void 0, void 0, function* () {
            const syncStream = yield this.sync();
            const result = yield syncStream.pull(filename);
            yield syncStream.close();
            return result;
        });
    }
    doAuth(authResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            if (authResponse.header.cmd !== 'AUTH') {
                throw new Error('Not an AUTH response');
            }
            if (authResponse.header.arg0 !== 1) {
                throw new Error(`
          Invalid AUTH parameter. Expected 1 and received ${authResponse.header.arg0}`);
            }
            if (!authResponse.data) {
                throw new Error('AUTH message doens\'t contain data');
            }
            let token = authResponse.data.buffer;
            // Try signing with one of the stored keys
            const keys = yield this.keyStore.loadKeys();
            for (const key of keys) {
                const signed = yield crypto.subtle.sign('RSASSA-PKCS1-v1_5', key.privateKey, token);
                const signatureMessage = Message.authSignature(new DataView(signed), this.options.useChecksum);
                yield this.sendMessage(signatureMessage);
                const signatureResponse = yield this.awaitMessage();
                if (signatureResponse.header.cmd === 'CNXN') {
                    return signatureResponse;
                }
                console.log('Received message ', signatureResponse, 'from phone');
            }
            // None of they saved Keys is usable. Create new key
            const key = yield AdbClient.generateKey(this.options.dump, this.options.keySize);
            yield this.keyStore.saveKey(key);
            const exportedKey = new DataView(yield crypto.subtle.exportKey('spki', key.publicKey));
            const keyMessage = Message.authPublicKey(exportedKey, this.options.useChecksum);
            yield this.sendMessage(keyMessage);
            console.log('Accept Key on Device');
            const keyResponse = yield this.awaitMessage();
            if (keyResponse.header.cmd !== 'CNXN') {
                console.error('AUTH failed. Phone didn\'t accept key', keyResponse);
                throw new Error('AUTH failed. Phone didn\'t accept key');
            }
            return keyResponse;
        });
    }
    sendMessage(m) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.messageChannel.write(m);
        });
    }
    static generateKey(dump, keySize) {
        return __awaiter(this, void 0, void 0, function* () {
            const extractable = dump;
            const key = yield crypto.subtle.generateKey({
                name: 'RSASSA-PKCS1-v1_5',
                modulusLength: keySize,
                publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
                hash: { name: 'SHA-1' }
            }, extractable, ['sign', 'verify']);
            if (dump) {
                yield privateKeyDump(key);
            }
            return key;
        });
    }
}
//# sourceMappingURL=AdbClient.js.map